import type {
  DecisionEngineJourneyDto,
  PdlAffordabilityJourneyDto,
  PdlAffordabilityRemarkDto,
} from '../api/admin';
import { humanizePdlBindingWhy } from './pdlAdjudicatorCopy';
import { PDL_ADJUDICATOR_DECLINE_CAUSE } from './pdlAdjudicatorDeclineCopy';

const PTI = 0.4;
const TDS = 0.5;
const PRODUCT_MIN = 100;
const PRODUCT_MAX = 1000;

export type PdlEligibilityStep = {
  step: number;
  title: string;
  math: string;
  result: string;
  /** When true, highlight as the binding ceiling in the checklist. */
  binding?: boolean;
};

export type PdlAmountComparison = {
  requested: string | null;
  approved: string | null;
  why: string | null;
};

export type PdlEligibilityExplanation = {
  decision: string;
  income: string;
  existingDebt: string;
  amountComparison: PdlAmountComparison;
  steps: PdlEligibilityStep[];
  finalLine: string;
  /** Active soft signals only — empty when none fired. */
  activeSoftSignals: string[];
  /** Plain-language decline cause when decision is DECLINE. */
  declineCause: string | null;
};

const SOFT_SIGNAL_LABELS: Record<string, (value?: string) => string> = {
  ELOD_UNAVAILABLE: () => 'ELOD unavailable — assumed $0 existing debt',
  PAYDAY_STACKING: (v) => (v ? `${v} payday lenders on account` : 'Payday stacking'),
  DEBT_NSF: (v) => (v ? `${v} returned payment(s) in 90 days` : 'Returned payments (NSF)'),
  DEBT_SERVICE_RATIO: (v) =>
    v && Number.isFinite(Number(v)) ? `Debt ratio ${Math.round(Number(v) * 100)}%` : 'High debt ratio',
  LOAN_VELOCITY: (v) => (v ? `${v} recent loan(s) in 90 days` : 'Recent loan activity'),
};

const DECLINE_CAUSE: Record<string, string> = PDL_ADJUDICATOR_DECLINE_CAUSE;

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function traceDollars(
  pdl: PdlAffordabilityJourneyDto | null | undefined,
  code: string,
): number | null {
  const step = pdl?.affordabilityTrace?.find((s) => s.code === code);
  const v = step?.value;
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  if (code.endsWith('_CENTS')) return v / 100;
  return v;
}

function remarkMessage(
  remarks: PdlAffordabilityRemarkDto[] | undefined,
  codes: string[],
): string | null {
  const hit = remarks?.find((r) => codes.includes(r.code));
  return hit?.message?.trim() || null;
}

function formatSoftSignal(code: string): string {
  const [head, tail] = code.split(':');
  const labeler = SOFT_SIGNAL_LABELS[head ?? code];
  if (labeler) return labeler(tail);
  return code.replace(/_/g, ' ').toLowerCase();
}

function resolveDeclineCause(
  reason: string | null | undefined,
  maxEligible: number,
  computedRoom: number,
): string {
  const key = (reason ?? '').trim().toUpperCase();
  if (key && DECLINE_CAUSE[key]) return DECLINE_CAUSE[key]!;
  if (maxEligible < PRODUCT_MIN) {
    return `Payment room ${fmt(computedRoom)}/mo is below $${PRODUCT_MIN} minimum`;
  }
  return 'Hard stop — see decline reason above';
}

function resolveAmountWhy(
  pdl: PdlAffordabilityJourneyDto | null | undefined,
  requested: number | null,
  maxEligible: number,
  legacyLimit: number | null,
  affordabilityMax: number | null,
): string | null {
  const fromRemark =
    remarkMessage(pdl?.remarks, [
      'BINDING_LEGACY_PRA',
      'BINDING_AFFORDABILITY',
      'CAPPED_TO_REQUESTED',
    ]) ??
    pdl?.capacityReason?.trim() ??
    null;
  if (fromRemark) {
    return humanizePdlBindingWhy(fromRemark, {
      requested,
      maxEligible,
      legacyLimit,
      affordabilityMax,
    });
  }

  if (requested != null && maxEligible > 0 && maxEligible < requested) {
    if (legacyLimit != null && legacyLimit === maxEligible) {
      return (
        `Approved ${fmt(maxEligible)} because the Cash4You pre-approval table limit (${fmt(legacyLimit)}) ` +
        `is lower than what the customer requested (${fmt(requested)}).`
      );
    }
    if (affordabilityMax != null && affordabilityMax === maxEligible) {
      return (
        `Approved ${fmt(maxEligible)} because bank payment room (${fmt(affordabilityMax)}) ` +
        `is lower than what the customer requested (${fmt(requested)}).`
      );
    }
    return `Offer is ${fmt(maxEligible)} because that is lower than the requested ${fmt(requested)}.`;
  }
  if (requested != null && maxEligible === requested) {
    return 'Approved amount matches what the customer requested.';
  }
  if (maxEligible >= PRODUCT_MAX) {
    return `Capped at payday product maximum ($${PRODUCT_MAX}).`;
  }
  return null;
}

export function buildPdlEligibilityExplanation(
  decisionEngine?: DecisionEngineJourneyDto | null,
  applicationDeclineReason?: string | null,
): PdlEligibilityExplanation | null {
  const pdl = decisionEngine?.pdlAffordability;
  const income =
    pdl?.monthlyIncome ??
    decisionEngine?.monthlyIncome ??
    null;
  const decision = (decisionEngine?.decision ?? '').toUpperCase() || '—';
  const declineKey = (applicationDeclineReason ?? decisionEngine?.declinedReason ?? '')
    .trim()
    .toUpperCase();

  if (typeof income !== 'number' || !Number.isFinite(income) || income <= 0) {
    if (decision === 'DECLINE' || declineKey) {
      const cause =
        DECLINE_CAUSE[declineKey] ??
        (declineKey ? declineKey.replace(/_/g, ' ').toLowerCase() : 'Evaluation could not run');
      return {
        decision: decision === '—' ? 'DECLINE' : decision,
        income: '—',
        existingDebt: '—',
        amountComparison: { requested: null, approved: null, why: cause },
        steps: [],
        finalLine: `Declined — ${cause}.`,
        activeSoftSignals: [],
        declineCause: cause,
      };
    }
    return null;
  }

  const debtCents = pdl?.existingMonthlyDebtServiceCents ?? 0;
  const debt = Math.max(0, debtCents / 100);
  const roomCents = pdl?.availableForNewPaymentCents;
  const room =
    typeof roomCents === 'number' && Number.isFinite(roomCents)
      ? roomCents / 100
      : Math.min(income * PTI, income * TDS - debt);

  const ptiCap = income * PTI;
  const tdsCap = Math.max(0, income * TDS - debt);
  const computedRoom = Math.min(ptiCap, tdsCap);

  const maxEligible = decisionEngine?.maxEligibleAmount ?? 0;
  const approved =
    typeof decisionEngine?.approvedAmount === 'number'
      ? decisionEngine.approvedAmount
      : maxEligible;
  const requested = decisionEngine?.requestedAmount ?? null;

  const legacyLimit = traceDollars(pdl, 'LEGACY_PRA_LIMIT');
  const affordabilityMax =
    traceDollars(pdl, 'AFFORDABILITY_MAX') ??
    (computedRoom > 0 ? Math.min(PRODUCT_MAX, Math.floor(computedRoom)) : 0);

  const bindingLegacy =
    legacyLimit != null &&
    affordabilityMax != null &&
    legacyLimit <= affordabilityMax &&
    legacyLimit === maxEligible;
  const bindingAffordability =
    legacyLimit != null &&
    affordabilityMax != null &&
    affordabilityMax < legacyLimit &&
    affordabilityMax === maxEligible;

  const steps: PdlEligibilityStep[] = [
    {
      step: 1,
      title: 'Verified bank income',
      math: 'Monthly take-home from income verification',
      result: `${fmt(income)}/mo`,
    },
    {
      step: 2,
      title: 'Existing loan payments (ELOD)',
      math: 'Monthly debt service from bank activity',
      result: `${fmt(debt)}/mo`,
    },
    {
      step: 3,
      title: 'Payment room (40% PTI cap)',
      math: `${fmt(income)} × 40%`,
      result: `${fmt(ptiCap)}/mo`,
    },
    {
      step: 4,
      title: 'Payment room (50% TDS cap)',
      math: `(${fmt(income)} × 50%) − ${fmt(debt)}`,
      result: `${fmt(tdsCap)}/mo`,
    },
    {
      step: 5,
      title: 'Usable payment room',
      math: `Lower of PTI and TDS`,
      result: `${fmt(typeof roomCents === 'number' ? room : computedRoom)}/mo`,
    },
    {
      step: 6,
      title: 'Bank affordability ceiling',
      math: 'Convert payment room to principal ($100–$1,000)',
      result: fmt(affordabilityMax ?? 0),
      binding: bindingAffordability,
    },
  ];

  if (legacyLimit != null && legacyLimit > 0) {
    steps.push({
      step: steps.length + 1,
      title: 'Cash4You pre-approval table limit',
      math: 'Scoring profile (table + income band + customer tier)',
      result: fmt(legacyLimit),
      binding: bindingLegacy,
    });
  }

  if (requested != null && requested > 0) {
    steps.push({
      step: steps.length + 1,
      title: 'Customer requested',
      math: 'Requested loan amount on application',
      result: fmt(requested),
    });
  }

  steps.push({
    step: steps.length + 1,
    title: 'Final offer',
    math: 'Minimum of all ceilings (and requested if lower)',
    result: maxEligible > 0 ? fmt(maxEligible) : fmt(0),
    binding: true,
  });

  const amountWhy = resolveAmountWhy(pdl, requested, maxEligible, legacyLimit, affordabilityMax);

  let finalLine: string;
  if (decision === 'DECLINE') {
    const cause = resolveDeclineCause(declineKey, maxEligible, computedRoom);
    finalLine = `Declined — ${cause}.`;
  } else if (requested != null && approved > 0 && approved < requested) {
    finalLine = `Approved ${fmt(approved)} — customer requested ${fmt(requested)}.`;
  } else if (requested != null && approved === requested) {
    finalLine = `Approved ${fmt(approved)} — matches requested amount.`;
  } else if (approved >= PRODUCT_MAX) {
    finalLine = `Approved ${fmt(approved)} — at payday maximum.`;
  } else {
    finalLine = `Approved ${fmt(approved)}.`;
  }

  const activeSoftSignals = (pdl?.reviewReasons ?? []).map(formatSoftSignal);

  return {
    decision,
    income: `${fmt(income)}/mo`,
    existingDebt: `${fmt(debt)}/mo`,
    amountComparison: {
      requested: requested != null && requested > 0 ? fmt(requested) : null,
      approved: approved > 0 ? fmt(approved) : maxEligible > 0 ? fmt(maxEligible) : null,
      why: amountWhy,
    },
    steps,
    finalLine,
    activeSoftSignals,
    declineCause: decision === 'DECLINE' ? resolveDeclineCause(declineKey, maxEligible, computedRoom) : null,
  };
}
